import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { onAuthStateChanged, signOut } from 'firebase/auth';
import { auth } from '@/lib/firebase';
import { formatNextRentalTime } from '@/lib/timezone';
import { getApiBaseUrl } from '@/lib/api';
import type {
  User,
  Watchlist,
  Recipient,
  CurrentRental,
  GetRentalHistoryResponse,
} from '@stilllovefilm/shared';

type SetupStatus =
  | 'loading'
  | 'incomplete'
  | 'ready'
  | 'has_rental'
  | 'rental_triggered';

export default function DashboardPage() {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [setupStatus, setSetupStatus] = useState<SetupStatus>('loading');
  const [user, setUser] = useState<User | null>(null);
  const [watchlist, setWatchlist] = useState<Watchlist | null>(null);
  const [recipients, setRecipients] = useState<Recipient[]>([]);
  const [currentRental, setCurrentRental] = useState<CurrentRental | null>(
    null
  );
  const [pastRentals, setPastRentals] = useState<CurrentRental[]>([]);
  const [isTriggering, setIsTriggering] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [idToken, setIdToken] = useState<string | null>(null);

  // Check auth state
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        const token = await user.getIdToken();
        setIdToken(token);
      } catch {
        navigate('/login');
      }
    });

    return () => unsubscribe();
  }, [navigate]);

  // Fetch all data
  const fetchData = useCallback(async () => {
    if (!idToken) return;

    try {
      // Fetch user, watchlist, recipients, current rental, and rental history in parallel
      const [userRes, watchlistRes, recipientsRes, rentalRes, historyRes] =
        await Promise.all([
          fetch(`${getApiBaseUrl()}/user`, {
            headers: { Authorization: `Bearer ${idToken}` },
          }),
          fetch(`${getApiBaseUrl()}/watchlist`, {
            headers: { Authorization: `Bearer ${idToken}` },
          }),
          fetch(`${getApiBaseUrl()}/recipients`, {
            headers: { Authorization: `Bearer ${idToken}` },
          }),
          fetch(`${getApiBaseUrl()}/rentals/current`, {
            headers: { Authorization: `Bearer ${idToken}` },
          }),
          fetch(`${getApiBaseUrl()}/rentals/history`, {
            headers: { Authorization: `Bearer ${idToken}` },
          }),
        ]);

      if (userRes.ok) {
        const data = await userRes.json();
        setUser(data.user);
      }

      if (watchlistRes.ok) {
        const data = await watchlistRes.json();
        setWatchlist(data.watchlist);
      }

      if (recipientsRes.ok) {
        const data = await recipientsRes.json();
        setRecipients(data.recipients);
      }

      if (rentalRes.ok) {
        const data = await rentalRes.json();
        setCurrentRental(data.rental);
      }

      if (historyRes.ok) {
        const data: GetRentalHistoryResponse = await historyRes.json();
        // Filter out the current rental from history (it's shown separately)
        // Past rentals are all rentals except the most recent one
        if (data.rentals.length > 1) {
          setPastRentals(data.rentals.slice(1));
        }
      }
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to load dashboard data'
      );
    } finally {
      setIsLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    if (idToken) {
      fetchData();
    }
  }, [idToken, fetchData]);

  // Determine setup status
  useEffect(() => {
    if (isLoading) {
      setSetupStatus('loading');
      return;
    }

    if (currentRental) {
      setSetupStatus('has_rental');
      return;
    }

    // Check if first rental is being generated (pending state)
    if (user?.firstRentalPendingSince) {
      setSetupStatus('rental_triggered');
      return;
    }

    // Watchlist is considered "accessible" if it has synced films
    // Note: letterboxdUrl is no longer required - sync mode can work with CSV/bookmarklet
    const hasAccessibleWatchlist =
      watchlist !== null && watchlist.filmsCount > 0;
    const hasVerifiedRecipient = recipients.some((r) => r.isVerified);

    if (hasAccessibleWatchlist && hasVerifiedRecipient) {
      setSetupStatus('ready');
    } else {
      setSetupStatus('incomplete');
    }
  }, [isLoading, watchlist, recipients, currentRental, user]);

  const handleTriggerFirstRental = async () => {
    if (!idToken) return;

    setIsTriggering(true);
    setError(null);

    try {
      const response = await fetch(`${getApiBaseUrl()}/rentals/trigger-first`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${idToken}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to create rental');
      }

      // Show the "check your inbox" state
      // The rental is being generated in the background
      setSetupStatus('rental_triggered');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create rental');
    } finally {
      setIsTriggering(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/login');
    } catch {
      setError('Failed to sign out');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center">
        <div className="text-center">
          <svg
            className="animate-spin h-8 w-8 text-primary mx-auto mb-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <p className="text-dark-500 font-body">Loading...</p>
        </div>
      </div>
    );
  }

  // Watchlist is considered "accessible" if it has synced films
  // Note: letterboxdUrl is no longer required - sync mode can work with CSV/bookmarklet
  const hasAccessibleWatchlist = watchlist !== null && watchlist.filmsCount > 0;
  const hasVerifiedRecipient = recipients.some((r) => r.isVerified);

  return (
    <div className="min-h-screen bg-cream">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <nav className="flex items-center justify-between">
            <h1 className="text-xl sm:text-2xl font-display font-bold text-primary">
              Still Love Film
            </h1>
            <div className="flex items-center gap-2 sm:gap-4">
              <Link
                to="/settings"
                className="text-dark-500 active:text-primary sm:hover:text-primary transition-colors font-body text-sm sm:text-base min-h-[44px] px-2 flex items-center touch-manipulation"
              >
                Settings
              </Link>
              <button
                onClick={handleSignOut}
                className="text-dark-500 active:text-primary sm:hover:text-primary transition-colors font-body text-sm sm:text-base min-h-[44px] px-2 touch-manipulation"
              >
                Sign out
              </button>
            </div>
          </nav>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-6 sm:py-8">
        <div className="max-w-2xl mx-auto">
          {/* Error Display */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-600 text-sm">{error}</p>
              <button
                onClick={() => setError(null)}
                className="text-red-500 text-sm underline mt-1"
              >
                Dismiss
              </button>
            </div>
          )}

          {/* Incomplete Setup State */}
          {setupStatus === 'incomplete' && (
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 text-center">
              <div className="w-14 h-14 sm:w-16 sm:h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4 sm:mb-6">
                <svg
                  className="w-7 h-7 sm:w-8 sm:h-8 text-yellow-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
              </div>
              <h2 className="text-xl sm:text-2xl font-display font-bold text-dark mb-3 sm:mb-4">
                Complete Your Setup
              </h2>
              <p className="text-dark-500 font-body mb-6 sm:mb-8 text-sm sm:text-base">
                You need to complete a few steps before you can receive your
                film picks.
              </p>

              {/* Setup Checklist */}
              <div className="text-left max-w-md mx-auto space-y-3 sm:space-y-4 mb-6 sm:mb-8">
                {/* Watchlist Status */}
                <div className="flex items-start gap-3 p-3 sm:p-4 bg-dark-50 rounded-lg">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      hasAccessibleWatchlist
                        ? 'bg-green-100 text-green-600'
                        : 'bg-dark-200 text-dark-400'
                    }`}
                  >
                    {hasAccessibleWatchlist ? (
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <span className="text-sm font-bold">1</span>
                    )}
                  </div>
                  <div>
                    <h3 className="font-medium text-dark">Add a watchlist</h3>
                    <p className="text-sm text-dark-500">
                      {hasAccessibleWatchlist
                        ? `Your watchlist has ${watchlist?.filmsCount || 0} film(s)`
                        : 'Connect your Letterboxd watchlist to get film picks'}
                    </p>
                  </div>
                </div>

                {/* Recipient Status */}
                <div className="flex items-start gap-3 p-3 sm:p-4 bg-dark-50 rounded-lg">
                  <div
                    className={`w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 ${
                      hasVerifiedRecipient
                        ? 'bg-green-100 text-green-600'
                        : 'bg-dark-200 text-dark-400'
                    }`}
                  >
                    {hasVerifiedRecipient ? (
                      <svg
                        className="w-4 h-4"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    ) : (
                      <span className="text-sm font-bold">2</span>
                    )}
                  </div>
                  <div>
                    <h3 className="font-medium text-dark">
                      Verify a recipient
                    </h3>
                    <p className="text-sm text-dark-500">
                      {hasVerifiedRecipient
                        ? `You have ${recipients.filter((r) => r.isVerified).length} verified recipient(s)`
                        : 'Verify your email to receive film picks'}
                    </p>
                  </div>
                </div>
              </div>

              <Link
                to="/settings"
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 sm:px-8 py-3 sm:py-4 text-white font-medium active:bg-primary-800 sm:hover:bg-primary-700 transition-colors min-h-[44px] touch-manipulation"
              >
                Go to Settings
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </Link>
            </div>
          )}

          {/* Ready for First Rental State */}
          {setupStatus === 'ready' && (
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 text-center">
              {/* Envelope illustration */}
              <div className="mb-2 sm:mb-4">
                <div className="relative inline-block">
                  <div className="relative w-32 h-24 sm:w-40 sm:h-28 mx-auto mt-8 sm:mt-12">
                    <div className="absolute inset-0 bg-primary rounded-lg shadow-xl">
                      <div className="absolute -top-0 left-0 right-0 h-1/2">
                        <div className="absolute inset-0 bg-primary rounded-t-lg" />
                        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4rem] border-r-[4rem] border-t-[2.75rem] sm:border-l-[5rem] sm:border-r-[5rem] sm:border-t-[3.5rem] border-l-transparent border-r-transparent border-t-primary-700" />
                      </div>
                      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-cream rounded-full border-4 border-primary-700 flex items-center justify-center shadow-inner">
                        <span className="text-primary-700 font-display font-bold text-[10px] sm:text-xs">
                          SLF
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <h2 className="text-xl sm:text-2xl font-display font-bold text-dark mb-3 sm:mb-4">
                You're all set!
              </h2>
              <p className="text-dark-500 font-body mb-6 sm:mb-8 max-w-md mx-auto text-sm sm:text-base">
                Your first film pick is ready to be chosen. Would you like to
                get it now, or wait until Friday?
              </p>

              {/* Choice buttons */}
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
                <button
                  onClick={handleTriggerFirstRental}
                  disabled={isTriggering}
                  className="inline-flex items-center justify-center gap-2 rounded-lg bg-primary px-6 sm:px-8 py-3 sm:py-4 text-white font-medium active:bg-primary-800 sm:hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] touch-manipulation"
                >
                  {isTriggering ? (
                    <>
                      <svg
                        className="animate-spin h-5 w-5"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      Selecting your film...
                    </>
                  ) : (
                    <>
                      Get it now
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M13 7l5 5m0 0l-5 5m5-5H6"
                        />
                      </svg>
                    </>
                  )}
                </button>
                <Link
                  to="/settings"
                  className="inline-flex items-center justify-center gap-2 rounded-lg border-2 border-dark-200 px-6 sm:px-8 py-3 sm:py-4 text-dark-600 font-medium active:bg-dark-50 sm:hover:bg-dark-50 transition-colors min-h-[44px] touch-manipulation"
                >
                  Wait until Friday
                </Link>
              </div>

              <p className="text-xs sm:text-sm text-dark-400 mt-4 sm:mt-6">
                Rentals will be sent to{' '}
                {recipients.filter((r) => r.isVerified).length} verified
                recipient(s)
              </p>
            </div>
          )}

          {/* Rental Triggered State - Check Your Inbox */}
          {setupStatus === 'rental_triggered' && (
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 text-center">
              {/* Email icon */}
              <div className="mb-6 sm:mb-8">
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto">
                  <svg
                    className="w-10 h-10 sm:w-12 sm:h-12 text-green-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                    />
                  </svg>
                </div>
              </div>

              <h2 className="text-xl sm:text-2xl font-display font-bold text-dark mb-3 sm:mb-4">
                Check your inbox!
              </h2>
              <p className="text-dark-500 font-body mb-6 sm:mb-8 max-w-md mx-auto text-sm sm:text-base">
                Your first rental is being prepared. You'll receive an email
                shortly with your mystery film envelope.
              </p>

              <div className="bg-dark-50 rounded-lg p-4 sm:p-6 max-w-md mx-auto">
                <p className="text-sm text-dark-600">
                  <span className="font-medium">Sending to:</span>{' '}
                  {recipients
                    .filter((r) => r.isVerified)
                    .map((r) => r.emailAddress)
                    .join(', ')}
                </p>
              </div>

              <p className="text-xs sm:text-sm text-dark-400 mt-6">
                Didn't receive it? Check your spam folder or{' '}
                <Link
                  to="/settings"
                  className="text-primary underline active:text-primary-800 sm:hover:text-primary-700"
                >
                  verify your email settings
                </Link>
                .
              </p>
            </div>
          )}

          {/* Has Rental State */}
          {setupStatus === 'has_rental' && currentRental && (
            <div className="bg-white rounded-2xl shadow-lg p-6 sm:p-8 text-center">
              {/* Movie poster or envelope illustration */}
              <div className="mb-6 sm:mb-8">
                {currentRental.openedAt ? (
                  <div className="relative inline-block">
                    <img
                      src={currentRental.movieData.poster}
                      alt={`${currentRental.movieTitle} poster`}
                      className="w-32 sm:w-40 rounded-lg shadow-xl object-cover"
                    />
                  </div>
                ) : (
                  <div className="relative inline-block">
                    <div className="relative w-32 h-24 sm:w-40 sm:h-28 mx-auto mt-8 sm:mt-12">
                      <div className="absolute inset-0 bg-primary rounded-lg shadow-xl">
                        <div className="absolute -top-0 left-0 right-0 h-1/2">
                          <div className="absolute inset-0 bg-primary rounded-t-lg" />
                          <div className="absolute top-0 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[4rem] border-r-[4rem] border-t-[2.75rem] sm:border-l-[5rem] sm:border-r-[5rem] sm:border-t-[3.5rem] border-l-transparent border-r-transparent border-t-primary-700" />
                        </div>
                        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 sm:w-10 sm:h-10 bg-cream rounded-full border-4 border-primary-700 flex items-center justify-center shadow-inner">
                          <span className="text-primary-700 font-display font-bold text-[10px] sm:text-xs">
                            SLF
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <h2 className="text-xl sm:text-2xl font-display font-bold text-dark mb-3 sm:mb-4">
                {currentRental.openedAt
                  ? 'Your Current Rental'
                  : 'You have a rental waiting!'}
              </h2>

              {currentRental.openedAt ? (
                <>
                  <p className="text-lg sm:text-xl text-primary font-display font-bold mb-2">
                    {currentRental.movieTitle} ({currentRental.movieYear})
                  </p>
                  <p className="text-dark-500 font-body mb-6 sm:mb-8 text-sm sm:text-base">
                    Revealed on{' '}
                    {new Date(currentRental.openedAt).toLocaleDateString(
                      'en-US',
                      {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      }
                    )}
                  </p>
                </>
              ) : (
                <p className="text-dark-500 font-body mb-6 sm:mb-8 max-w-md mx-auto italic text-sm sm:text-base">
                  "{currentRental.teaserText}"
                </p>
              )}

              <Link
                to={`/reveal/${currentRental.token}`}
                className="inline-flex items-center gap-2 rounded-lg bg-primary px-6 sm:px-8 py-3 sm:py-4 text-white font-medium active:bg-primary-800 sm:hover:bg-primary-700 transition-colors min-h-[44px] touch-manipulation"
              >
                {currentRental.openedAt ? 'View Again' : 'Open Envelope'}
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M13 7l5 5m0 0l-5 5m5-5H6"
                  />
                </svg>
              </Link>

              <p className="text-xs sm:text-sm text-dark-400 mt-4 sm:mt-6">
                Next rental:{' '}
                {user
                  ? formatNextRentalTime(user.timezone)
                  : 'Friday at 9:00 AM'}
              </p>
            </div>
          )}

          {/* Past Rentals Section */}
          {pastRentals.length > 0 && (
            <div className="mt-6 sm:mt-8">
              <h3 className="text-lg font-display font-bold text-dark mb-4">
                Past Rentals
              </h3>
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3 sm:gap-4">
                {pastRentals.map((rental) => (
                  <Link
                    key={rental.id}
                    to={`/reveal/${rental.token}`}
                    className="group"
                  >
                    <div className="relative aspect-[2/3] rounded-lg overflow-hidden shadow-md group-hover:shadow-lg transition-shadow">
                      {rental.openedAt ? (
                        <img
                          src={rental.movieData.poster}
                          alt={`${rental.movieTitle} poster`}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full bg-primary flex items-center justify-center">
                          <span className="text-white font-display font-bold text-xs">
                            ?
                          </span>
                        </div>
                      )}
                    </div>
                    <p className="mt-1 text-xs text-dark-600 truncate text-center">
                      {rental.openedAt
                        ? `${rental.movieTitle} (${rental.movieYear})`
                        : 'Unopened'}
                    </p>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Quick Links */}
          <div className="mt-6 sm:mt-8 flex justify-center">
            <Link
              to="/settings"
              className="text-dark-500 active:text-primary sm:hover:text-primary transition-colors font-body text-sm min-h-[44px] flex items-center touch-manipulation"
            >
              Manage watchlists and recipients
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
